import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ComputedRef, ref, Ref, watch } from "vue";
import {
    EventsDrilldownDetailEvent,
    EventsDrilldownDetailEventCollection,
    EventsDrilldownDetailEventMarket,
} from "@/live/model/events-drilldown-detail-event";
import { useLiveSportsStore } from "@/live/stores/live-sports";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import useMarketGrouping from "@/live/composables/use-market-grouping";
import { MarketGroup } from "@/shared/model/market";
import { useFavouritesStore } from "@/shared/stores/use-favourites-store";
import { Status } from "@/shared/model/enums/status";
import { DrilldownNode } from "@/shared/model/drilldown-node";
import { logException } from "@/shared/utils/logger/use-app-logger";
import { waitUntilTrue } from "@/shared/utils/wait";

const FETCH_INTERVAL = 10000;

export const useLiveEventDetailStore = defineStore("LiveEventDetail", () => {
    const { getEventsDrilldownDetail } = useOpenbetApi();
    const { orderOutcomesInToGroups } = useMarketGrouping();
    const liveSportsStore = useLiveSportsStore();
    const favouritesStore = useFavouritesStore();

    // state
    const initialized: Ref<boolean> = ref(false);
    const fetchInterval: Ref<number> = ref(0);
    const eventId: Ref<string> = ref();
    const event: Ref<EventsDrilldownDetailEvent> = ref<EventsDrilldownDetailEvent>();
    const eventIsOver: Ref<boolean> = ref(false);
    const eventDoesNotExist: Ref<boolean> = ref(false);
    const eventLoaded: Ref<boolean> = ref(false);
    const activeSportId: Ref<string> = ref();
    const selectedCollectionId: Ref<string> = ref();

    // getters
    const collections: ComputedRef<EventsDrilldownDetailEventCollection[]> = computed(() => {
        return (
            event.value?.collections
                .map((item) => ({ ...item, displayOrder: item.displayOrder || 3000 }))
                .toSorted((a, b) => a.displayOrder - b.displayOrder) || []
        );
    });
    const collectionsDict: ComputedRef<{ [key: string]: EventsDrilldownDetailEventCollection }> = computed(() => {
        const newCollectionsDict = {};
        if (!event.value) {
            return newCollectionsDict;
        }
        // process all collections
        for (const col of collections.value) {
            const groupedMarketsSorted: Array<EventsDrilldownDetailEventMarket | MarketGroup> = [];
            const groupedMarketsToSort = [...event.value.groupedMarkets].filter((item) => item.outcomes.length);
            for (const groupedMarket of groupedMarketsToSort) {
                const market = {
                    ...groupedMarket,
                    orderedOutcomes: orderOutcomesInToGroups(groupedMarket, event.value.teams),
                };
                if (market.id && col.marketIds.includes(market.id)) {
                    groupedMarketsSorted.push(market);
                    continue;
                }

                if (!market.displayOrder) {
                    market.displayOrder = market.marketDisplayOrder;
                }

                const marketIds: string[] = market.outcomes.map((item) => item.marketId);

                for (const id of marketIds) {
                    if (col.marketIds.includes(id)) {
                        groupedMarketsSorted.push(market);
                        break;
                    }
                }
            }

            const sortedMarkets = favouritesStore.sortMarkets(groupedMarketsSorted);

            newCollectionsDict[col.id] = {
                ...col,
                markets: sortedMarkets,
            };
        }
        return newCollectionsDict;
    });
    const eventIsActive: ComputedRef<boolean> = computed(
        () => event.value?.active && event.value?.status === Status.Active,
    );

    // actions
    const waitUntilInitialized = async (time: number = 100, timeout: number = 10000) => {
        await waitUntilTrue(initialized, time, timeout).catch(() => {
            logException("live-event-detail", undefined, {
                message: `Store initialization timed out. Timeout interval ${timeout} breached.`,
            });
        });
    };
    const setFetchInterval = (interval: number) => {
        fetchInterval.value = interval;
    };
    const setEventId = (id: string) => {
        eventId.value = id;
    };
    const fetchEventDetail = async (eventId: string) => {
        if (!eventId) return;
        let sport: DrilldownNode | null = null;

        try {
            // In comparison with getSazkaEventsList getEventsDrilldownDetail returns full list of markets... getSazkaEventsList returns only subset
            const response = await getEventsDrilldownDetail(
                {
                    eventIds: Number(eventId),
                    groupedMarkets: true,
                },
                { wsData: { update: true } },
            );

            const responseEvent = response[0];

            if (!responseEvent && event.value) {
                eventIsOver.value = true;
            } else if ((!responseEvent && !event.value) || !responseEvent.liveNow) {
                eventDoesNotExist.value = true;
            } else {
                event.value = responseEvent;
                sport = responseEvent.drilldownNodes[responseEvent.drilldownNodes.length - 1];
                for (const group of event.value.groupedMarkets) {
                    group.orderedOutcomes = orderOutcomesInToGroups(group, event.value.teams);
                }
                if (!selectedCollectionId.value) {
                    const collectionsSorted = event.value.collections.toSorted(
                        (a, b) => (a.displayOrder || 5000) - (b.displayOrder || 5000),
                    );
                    collectionSelected(collectionsSorted[0].id);
                }
            }
        } catch (error) {
            logException("live-event-detail", error, {
                message: "An error has occurred while fetching event detail",
                eventId: eventId,
            });
        } finally {
            if (!activeSportId.value) {
                activeSportId.value = sport?.id;
                if (sport) liveSportsStore.setActiveSportId(sport.id);
            }
            initialized.value = true;
            eventLoaded.value = true;
        }
    };
    const collectionSelected = (id: string) => {
        selectedCollectionId.value = id;
    };
    const $reset = () => {
        initialized.value = false;
        fetchInterval.value = 0;
        eventId.value = undefined;
        event.value = undefined;
        eventIsOver.value = false;
        eventDoesNotExist.value = false;
        eventLoaded.value = false;
        activeSportId.value = undefined;
        selectedCollectionId.value = undefined;
    };

    // additional logic
    let fetchIntervalId = null;
    watch(
        fetchInterval,
        (newValue) => {
            if (newValue) {
                fetchIntervalId = setInterval(async () => {
                    await fetchEventDetail(eventId.value);
                }, fetchInterval.value);
            } else if (fetchIntervalId) {
                clearInterval(fetchIntervalId);
            }
        },
        { immediate: true },
    );

    watch(
        eventId,
        async (newValue) => {
            if (!newValue) {
                $reset();
            } else if (newValue && fetchInterval.value === 0) {
                await fetchEventDetail(newValue);
                setFetchInterval(FETCH_INTERVAL);
            }
        },
        { immediate: true },
    );

    watch(eventIsOver, (newValue) => {
        if (newValue) {
            setFetchInterval(0);
        }
    });

    return {
        initialized,
        fetchInterval,
        eventId,
        event,
        eventIsOver,
        eventDoesNotExist,
        eventLoaded,
        activeSportId,
        selectedCollectionId,
        collections,
        collectionsDict,
        eventIsActive,
        waitUntilInitialized,
        setFetchInterval,
        setEventId,
        fetchEventDetail,
        collectionSelected,
        $reset,
    };
});

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useLiveEventDetailStore, import.meta.hot));
}
